import { UserRoleInput } from '@modules/activate/types';
import { Category } from '@modules/common/types';
import {
  EditMetadataInput,
  IntegrationDetails,
} from '@modules/integrationsV2/types';
import { UpdateMetadataRequest } from '@modules/metadataManagement/types';
import {
  InAppNotificationCardEventProps,
  InAppNotificationListEventProps,
} from '@modules/notifications/inApp/analytics/events';
import { NotificationSettingsEventProps } from '@modules/notifications/settings/analytics/events';
import { SortBy } from '@modules/quantify/store/types';
import { GridRowId } from '@mui/x-data-grid-pro';
import { captureMessage } from '@sentry/react';
import { Dayjs } from 'dayjs';
import {
  AnalysisUseCase,
  ArtefactEditableByFilter,
  ArtefactSpecificFilter,
  ArtefactType,
  AssistantSampleQuestionType,
  EqlField,
  EqlQueryProperties,
  IntegrationChannelType,
  IntegrationType,
  ObjectType,
  PredictionManipulation,
  QuantifyVisualisationType,
  SlackChannelInput,
  Status,
  SuggestionDataInput,
  TimeUnit,
  UserRole,
} from 'generated/graphql';
import { Userpilot } from 'userpilot';
import {
  FeedbackContentType,
  FeedbackLabel,
  FeedbackSourceMetadata,
} from 'v2/core/entities/feedback';
import { FieldName } from 'v2/core/entities/fields';
import { Scope } from 'v2/core/entities/scope';
import { User } from 'v2/core/entities/user';
import { UUID } from 'v2/core/entities/uuid';
import ErrorCode from 'v2/core/errors/codes';
import { SubjectType } from 'v2/core/repository/edge-server/types';
import QBAction from 'v2/querybuilder/types/querybuilderaction';
import {
  getDefaultAnalyticsOptions,
  shouldLogAnalyticsEvents,
  shouldSendAnalyticsEvents,
} from './analytics';
import { CollectionEvents } from './collectionEvents';
import { CustomFilterEvents } from './customFiltersEvents';
import { DashboardV2Events } from './dashboardV2Events';
import { SearchAnalysesWidgetsEvents } from './searchAnalysesWidgetsEvents';
import { TaxonomyV2Events } from './taxonomyV2Events';

export interface EventProps {
  eventName: string;
}

export type NavbarItemClickedEventName =
  | 'Home Clicked'
  | 'Delta Analysis Clicked'
  | 'Rules Clicked'
  | 'Thematic Analysis Clicked'
  | 'Taxonomy Clicked'
  | 'Semantic Search Clicked'
  | 'Dashboards Clicked'
  | 'Integrations Clicked'
  | 'View Profile Clicked'
  | 'Invite Teammate Clicked'
  | 'Switch Tenant Clicked'
  | 'Quick Menu Clicked'
  | 'Support Tab Clicked'
  | 'Quantify Clicked'
  | 'Compare Clicked'
  | 'Search Clicked'
  | 'Notifications Clicked'
  | 'Explore Clicked'
  | 'Saved Artefacts Clicked'
  | 'Manage Metadata Clicked'
  | 'Admin Settings Clicked'
  | 'Helpcenter Link Clicked'
  | 'Changelog Link Clicked'
  | 'Tenant Avatar Menu Opened'
  | 'Switch To Light Mode Clicked'
  | 'Switch To Dark Mode Clicked'
  | 'Intercom Help Clicked'
  | 'Wisdom Clicked'
  | 'Manage Unified Field Link Clicked';

export type ExploreTabClickedEventName =
  | 'Explore - Search Tab Clicked'
  | 'Explore - Quantify Tab Clicked'
  | 'Explore - Compare Tab Clicked'
  | 'Explore - Anomalies Tab Clicked';

export interface NavbarItemClickedEventProps extends EventProps {
  eventName: NavbarItemClickedEventName;
}

interface LogoutEventProps extends EventProps {
  eventName: 'Logout Clicked' | 'User Signed Out';
}

interface ForceLogoutEventProps extends EventProps {
  eventName: 'Forced User Logout';
  fromURL: string;
  operationName: string;
  errorCode?: ErrorCode;
}

interface SearchInputEventProps extends EventProps {
  eventName: 'Search Entered CTA';
  searchQuery: string;
}

interface DatePickerEventProps extends EventProps {
  eventName:
    | 'Datepicker - Opened'
    | 'Datepicker - Closed'
    | 'Datepicker - Applied'
    | 'Datepicker - Preset Option Selected';
  dateRange?: string | null;
  presetOption?: string;
}

interface TimeStepPickerEventProps extends EventProps {
  eventName: 'Time Step Picked';
  timeStep: string;
}

interface DownloadReportEventProps extends EventProps {
  eventName: 'Export Feedback CTA';
  type: 'records';
  eqlExpression: string;
  dateRange: string;
  aggregatePeriod: string;
  includeFeedbackContent?: boolean;
  includeOriginalFeedbackContent?: boolean;
  from:
    | 'Search Page'
    | 'Quantify Page'
    | 'Dashboard Page'
    | 'Feedback Drawer'
    | 'Collections Page';
}

interface ToggleNotebookViewEventProps extends EventProps {
  eventName: 'View Data' | 'View Feedback';
  queryString: string;
  dateRange: string;
  aggregatePeriod: string;
  pageName: string;
}

interface SelectChartTypeEventProps extends EventProps {
  eventName:
    | 'Breakdown Tab Clicked'
    | 'Donut Tab Clicked'
    | 'Time Series Tab Clicked'
    | 'Tabular Tab Clicked'
    | 'Stacked Bar Tab Clicked'
    | 'Time Series Normalised Tab Clicked'
    | 'Stacked Bar Normalised Tab Clicked'
    | 'Segregated Bar Tab Clicked'
    | 'Stacked Area Tab Clicked';
  field: FieldName;
  queryString: string;
  dateRange: string;
  aggregatePeriod: string;
}

interface FeedbackListSortByEventProps extends EventProps {
  eventName: 'Feedback List Sort By Changed';
  sortBy: string;
}

interface FeedbackViewActionEventProps extends EventProps {
  eventName:
    | 'Copy Record Link'
    | 'See Orginal Review/Conversation'
    | 'Hide Original Review/Conversation';
  recordID: string;
}

interface ConversationViewEventProps extends EventProps {
  eventName: 'Expand Conversation View' | 'Collapse Conversation View';
  recordID: string;
  view: 'original' | 'translated';
}

interface FetchRecordsEventProps extends EventProps {
  eventName: 'Records Fetched Successfully' | 'Records Fetched Failed';
  queryString: string;
  dateRange: string;
  aggregatePeriod: string;
}

interface FetchMoreRecordsEventProps extends EventProps {
  eventName: 'Fetch More Records';
  queryString: string;
  dateRange: string;
  aggregatePeriod: string;
  offset?: number;
}

interface TaxonomyTreeViewEventProps extends EventProps {
  eventName: 'Taxonomy Tree Expanded' | 'Taxonomy Tree Collapse';
  parentName: string;
}

interface TaxonomyTreeSelectEventProps extends EventProps {
  eventName: 'Taxonomy Page Item Selected' | 'Taxonomy Page Item Deselected';
  field?: FieldName;
  name: string;
}

interface NotebookEventProps extends EventProps {
  eventName: 'Add Widget' | 'Delete Widget';
  field: FieldName;
  name: string;
  queryString?: string;
  dateRange?: string;
  aggregatePeriod?: string;
}

interface QueryBuilderEventProps extends EventProps {
  eventName:
    | 'Query Builder Opened'
    | 'Query Builder Closed'
    | 'Query Builder Applied'
    | 'Query Builder Reset'
    | 'Query Builder Draft Changed';
  queryString?: string;
  dateRange?: string;
  aggregatePeriod?: string;
  action?: string;
}

interface QuickFilterEventProps extends EventProps {
  eventName:
    | 'Quick Filter Added'
    | 'Quick Filter View Feedback'
    | 'Quick Filter Merge Reasons';
  eqlExpression?: string;
  triggerType?: 'Chart' | 'Legend' | 'Quick Filter Tag Group' | 'Table';
}

interface ChartTooltipEventProps extends EventProps {
  eventName:
    | 'Tooltip Expanded'
    | 'Tooltip Collapsed'
    | 'Tooltip - View Feedback Opened'
    | 'Tooltip - View Feedback Closed'
    | 'Tooltip - Hide Button Clicked'
    | 'Tooltip - Theme Detail Clicked'
    | 'Tooltip - Entity Detail Clicked'
    | 'Tooltip - Reason Detail Clicked'
    | 'Legend - Show Button Clicked';
  fieldName: string;
  triggerNode: string | number;
  triggerType?: 'Chart' | 'Legend';
}

interface WidgetPaginationEventProps extends EventProps {
  eventName: 'Widget Page Changed';
  chartType: 'Breakdown' | 'TimeSeries' | 'Donut' | 'Tabular';
  fieldName: FieldName | string;
  page: number;
  startIndex: number;
  endIndex: number;
}

interface TaxonomyVisibilityEventProps extends EventProps {
  eventName: 'Taxonomy Visibility Applied';
  selected: {
    themes: string[];
    entities: string[];
    reasons: string[];
  };
  fieldName: string;
}

interface AutoFilterEventProps extends EventProps {
  eventName: 'Auto Filter Applied';
  selected: string[];
  fieldName: string;
  isVisibilityChange: boolean;
  metadataQueryKey?: string;
}

interface AutoFilterClickEventProps extends EventProps {
  eventName: 'Filter Out Correlations Clicked';
  fieldName: string;
}

interface SearchQueryHistoryEventProps extends EventProps {
  eventName: 'Search Query History Item Clicked';
  query: string;
}

interface ToggleSearchRelatedQueriesEventProps extends EventProps {
  eventName:
    | 'Search - Related Queries View Opened'
    | 'Search - Related Queries View Closed';
}

interface SignInEventProps extends EventProps {
  eventName: 'Sign In with Email CTA' | 'Sign in with Google CTA';
}

interface SignUpWithEmailEventProps extends EventProps {
  eventName: 'Sign Up with Email CTA';
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole | undefined;
}

interface SignUpWithGoogleEventProps extends EventProps {
  eventName: 'Sign up with Google CTA';
}

interface OTPViewNavEventProps extends EventProps {
  eventName:
    | 'Back from Submit OTP'
    | 'Change Email for New OTP'
    | 'Back from Forget Password';
  email?: string;
}

interface OnboardingEventProps extends EventProps {
  eventName: 'Account Created on Enterpret' | 'Continue from On-boarding CTA';
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface ForgotPasswordEventProps extends EventProps {
  eventName: 'Forgot Password';
}

interface ComplianceLinkEventProps extends EventProps {
  eventName: 'Terms of Services' | 'Privacy Policy';
}

interface theme {
  name?: string;
  description?: string;
  childEntities?: string[];
}

interface ThemeUpdatedEventProps extends EventProps {
  eventName:
    | 'Theme Name Updated'
    | 'Theme Description Updated'
    | 'Theme Definition Updated';
  id: string;
  old: theme;
  new: theme;
}

interface ThemeCreatedEventProps extends EventProps {
  eventName: 'Theme Created';
  id: string;
  theme: theme;
}

interface ThemeDeletedEventProps extends EventProps {
  eventName: 'Theme Deleted';
  id: string;
  theme: theme;
}

interface RedirectEventProps extends EventProps {
  eventName:
    | 'Redirecting to Theme Detail Page'
    | 'Redirecting to Reason Detail Page'
    | 'Redirecting to Entity Detail Page'
    | 'Redirecting to last Page';
  taxonomyFieldName?: string;
}

interface QueryContainerEventProps extends EventProps {
  eventName: 'Query Container Updated';
  queryString?: string;
  dateRange?: string;
  aggregatePeriod?: string;
}

interface AnalysisEventProps extends EventProps {
  eventName:
    | 'Analysis Saved'
    | 'Analysis Updated'
    | 'Analysis Renamed'
    | 'Analysis Duplicated';
  name: string;
  scope: Scope;
  link: string;
  queryBuilderString: string;
  dateRange: string;
  aggregatePeriod: string;
}

interface AnalysisResetEventProps extends EventProps {
  eventName: 'Analysis Reset';
  name: string;
  link: string;
}

interface AnalysisSharingEventProps extends EventProps {
  eventName:
    | 'Analysis Share Dialog Opened'
    | 'Analysis Share Dialog Closed'
    | 'Analysis Sharing Updated'
    | 'Analysis Link Copied';
  name: string;
  link: string;
  scope: Scope;
}

interface RuleSharingEventProps extends EventProps {
  eventName:
    | 'Rule Share Dialog Opened'
    | 'Rule Share Dialog Closed'
    | 'Rule Link Copied';
  name: string;
  link: string;
}
interface AnalysisArchiveEventProps extends EventProps {
  eventName: 'Analysis Archive' | 'Analysis Unarchive';
  link: string;
  analysisID: string;
}

interface analysisOpenedEventProps extends EventProps {
  eventName:
    | 'Analysis Opened From List Switcher'
    | 'Analysis Opened in New Tab From List Switcher'
    | 'Analysis Page Opened';
  name: string;
  link: string;
  owner?: string;
}

interface analysisCreateNew extends EventProps {
  eventName: 'Create New Analysis' | 'Discard Unsaved Analysis';
}

interface analysisSearch extends EventProps {
  eventName: 'Analysis List Searched';
  searchFilter: string;
}

interface analysisListTab extends EventProps {
  eventName: 'Analysis List Tab Changed';
  tab: string;
}

type AnalysisSwitcherEventProps =
  | analysisOpenedEventProps
  | analysisCreateNew
  | analysisSearch
  | analysisListTab;

interface AnalysisFeedbackEventProps extends EventProps {
  eventName:
    | 'Analysis Feedback Alerts Setup'
    | 'Analysis Feedback Alerts Removed'
    | 'Analysis Feedback Alerts Tested';
  name: string;
  link: string;
  channels: string[];
}

interface dashboardOpenedEventProps extends EventProps {
  eventName:
    | 'Dashboard Opened'
    | 'Dashboard Opened in New Tab'
    | 'Dashboard Link Copied From List';
  name: string;
  link: string;
}

interface dashboardListSearchEventProps extends EventProps {
  eventName: 'Search on Dashboard List';
  query: string;
}

interface dashboardListPaginationEventProps extends EventProps {
  eventName:
    | 'Dashboard List Next Page Clicked'
    | 'Dashboard List Prev Page Clicked';
  page: number;
}

type DashboardListEventProps =
  | dashboardOpenedEventProps
  | dashboardListSearchEventProps
  | dashboardListPaginationEventProps;

interface newDashboardEventProps extends EventProps {
  eventName: 'Dashboard Created' | 'Dashboard Duplicated';
  name: string;
  link: string;
}

interface dashboardCreateDialogEventProps extends EventProps {
  eventName:
    | 'Create Dashboard Dialog Opened'
    | 'Create Dashboard Dialog Closed';
}

type DashboardCreateEventProps =
  | newDashboardEventProps
  | dashboardCreateDialogEventProps;

interface dashboardAnalysisEventProps extends EventProps {
  eventName:
    | 'Analysis Expanded within Dashboard'
    | 'Analysis Collapsed within Dashboard'
    | 'Analysis Opened in New Tab from Dashboard';
  dashboardName: string;
  dashboardLink: string;
  analysisName: string;
  analysisLink: string;
}

interface dashboardUpdateDialogEventProps extends EventProps {
  eventName:
    | 'Manage Dashboard Analysis Dialog Opened'
    | 'Manage Dashboard Analysis Dialog Closed';
  name: string;
  link: string;
}

type ViewDashboardEventProps =
  | dashboardAnalysisEventProps
  | dashboardUpdateDialogEventProps;

interface dashboardRenameEventProps {
  eventName: 'Dashboard Renamed';
  name: string;
  link: string;
}

interface dashboardAnalysisUpdateEventProps {
  eventName: 'Dashboard Analyses Updated';
  name: string;
  link: string;
  analysisIDs: string[];
}

type DashboardUpdateEventProps =
  | dashboardRenameEventProps
  | dashboardAnalysisUpdateEventProps;

interface dashboardReportDialogProps extends EventProps {
  eventName:
    | 'Dashboard Report Dialog Opened'
    | 'Dashboard Report Dialog Closed';
  name: string;
  link: string;
}

interface dashboardReportAlertEventProps extends EventProps {
  eventName:
    | 'Dashboard Report Alerts Setup'
    | 'Dashboard Report Alerts Removed'
    | 'Dashboard Report Alerts Tested';
  name: string;
  link: string;
  channels: string[];
}

type DashboardReportEventProps =
  | dashboardReportDialogProps
  | dashboardReportAlertEventProps;

interface DashboardArchiveEventProps extends EventProps {
  eventName: 'Dashboard Archive' | 'Dashboard Unarchive';
  link: string;
}
interface DashboardSharingEventProps extends EventProps {
  eventName:
    | 'Dashboard Share Dialog Opened'
    | 'Dashboard Share Dialog Closed'
    | 'Dashboard Sharing Updated'
    | 'Dashboard Link Copied';
  name: string;
  link: string;
  scope: Scope;
}

interface InviteUser extends EventProps {
  eventName:
    | 'Invitations Sent'
    | 'Invitation Link Copied'
    | 'Invitation Login Page Opened';
  isSubjectInvite: boolean;
  sharableLink?: string;
  artefactUrl?: string;
  toEmails?: string[];
  subjectType?: SubjectType;
}

interface QueryBuilderActionEventProps extends EventProps {
  eventName: 'Query Builder Action Event';
  action: QBAction;
}

interface CommandPaletteEventProps extends EventProps {
  eventName: string;
  eventAction: string;
  eventLabel: string;
}

interface SemanticSearchEventProps extends EventProps {
  eventName:
    | 'Search Applied'
    | 'Update search time range'
    | 'Switched to Quick Search mode'
    | 'Switched to Query Builder mode'
    | 'Cleared search input';
  isQuickSearchMode: boolean;
  searchQuery: string;
  queryString: string;
  dateRange?: string;
  aggregatePeriod?: string;
}

interface ClearCacheEventProps extends EventProps {
  eventName: 'Clear Cache';
}

export interface FeedbackEventsProps {
  eventName:
    | 'Feedback Link Copied'
    | 'Feedback Text Selected'
    | 'Feedback External Source Link Clicked'
    | 'Feedback Metadata Copied'
    | 'Feedback Translation Changed'
    | 'Feedback Details Entity Highlighted'
    | 'Feedback Details Reason Highlighted'
    | 'Feedback Details Sentiment Highlighted'
    | 'Feedback Details Search Highlighted'
    | 'Feedback Drawer Previous Pagination Clicked'
    | 'Feedback Drawer Next Pagination Clicked'
    | 'Feedback Card Expanded'
    | 'Feedback Card Collapsed'
    | 'Feedback Page Viewed'
    | 'Feedback List - Scroll To Top Button Clicked'
    | 'Feedback Card Expanded in New Tab';
  selectedText?: string;
  metadata?: FeedbackSourceMetadata;
  label?: FeedbackLabel;
  feedbackID: string | undefined;
  sourceCreatedAt?: Dayjs;
  sourceName?: string;
}

export interface StatusEmailEventsProps {
  eventName:
    | 'User Interests Modal Opened'
    | 'User Interest Updated'
    | 'User Unsubscribed';
  unsubscribeReason?: string;
  selectedInterest?: string;
  scheduleID?: string;
}

export interface OnboardingEventsProps {
  eventName:
    | 'Use in Analysis clicked'
    | 'Skip Tour clicked'
    | 'Clicked on Get Started'
    | 'Clicked on What is Enterpret?'
    | 'Clicked on Feedback Sources'
    | 'Clicked on What is Taxonomy?'
    | 'Clicked on What is Theme?'
    | 'Clicked on What is Entity?'
    | 'Clicked on What is Reason?'
    | 'Theme Selected'
    | 'Reason Selected'
    | 'Resume Tour clicked'
    | 'Dismiss Tour clicked';
  from: 'Support Page' | 'Welcome Page' | 'Home Page';
  value?: string | null;
}

export interface AnalysisV3EventProps extends EventProps {
  eventName:
    | 'Analysis Saved'
    | 'Analysis Updated'
    | 'Analysis Renamed'
    | 'Analysis Duplicated';
  name: string;
  scope: Scope;
  link: string;
  query?: string;
  metadata: string;
  useCase: AnalysisUseCase;
  quantifyQueryOptions?: QuantifyQueryOptions;
}

interface AnalysisV3ResetEventProps extends EventProps {
  eventName: 'Analysis Reset';
  name: string;
  link: string;
  useCase: AnalysisUseCase;
}

interface AnalysisV3SharingEventProps extends EventProps {
  eventName:
    | 'Analysis Share Dialog Opened'
    | 'Analysis Share Dialog Closed'
    | 'Analysis Sharing Updated'
    | 'Analysis Link Copied';
  name: string;
  link: string;
  scope: Scope;
  useCase: AnalysisUseCase;
}

interface AnalysisV3ArchiveEventProps extends EventProps {
  eventName: 'Analysis Archive' | 'Analysis Unarchive';
  name: string;
  link: string;
  status: Status;
  useCase: AnalysisUseCase;
}

interface AnalysisV3OpenedEventProps extends EventProps {
  eventName: 'Analysis Opened' | 'Analysis Opened in New Tab';
  name: string;
  link: string;
  scope: Scope;
  useCase?: AnalysisUseCase;
}

interface CreateNewAnalysisV3EventProps extends EventProps {
  eventName: 'Create New Analysis';
  useCase: AnalysisUseCase;
}

interface AnalysisV3SearchEventProps extends EventProps {
  eventName: 'Analysis List Searched';
  searchText: string;
  useCase: AnalysisUseCase;
}

interface AnalysisV3ListTabEventProps extends EventProps {
  eventName: 'Analysis List Tab Changed';
  tab: string;
  useCase: AnalysisUseCase;
}

interface AnalysisV3ListTabPaginationEventProps extends EventProps {
  eventName:
    | 'Analysis List - Go to Next Page'
    | 'Analysis List - Go to Previous Page';
  page: number;
  useCase: AnalysisUseCase;
}

interface QuantifyQueryOptions {
  showMeLabel?: string;
  queryString?: string;
  shouldShowAnomalies?: boolean;
  duration?: string;
  measuredAsCriteria?: string;
  measuredAsFormula?: string;
  denominatorWhereClauseEQL?: string;
}

export interface QuantifyQueryEventProps extends EventProps {
  eventName:
    | 'Quantify - Execute Button Clicked'
    | 'Quantify - Reset Button Clicked'
    | 'Quantify - Query Executed'
    | 'Quantify - Reset Icon Clicked'
    | 'Quantify - Add Query Via Query Builder'
    | 'Quantify - Duration Selected'
    | 'Quantify - Formula Selected'
    | 'Quantify - Measured As Selected'
    | 'Quantify - Denominator WhereClause Changed'
    | 'Quantify - Group by Selected';
  query: QuantifyQueryOptions;
  name: string;
  link: string;
  scope: Scope;
}

interface QuantifyTemplatesEventProps extends EventProps {
  eventName:
    | 'Quantify Templates - "Top recommended Reasons for you" clicked'
    | 'Quantify Templates - "Top anomalous Reasons" clicked'
    | 'Quantify Templates - "Top Reasons for negative feedback" clicked'
    | 'Quantify Templates - "Top anomalous Tracked Keywords" clicked'
    | 'Quantify Templates - "Feature requests for top Tracked Keywords" clicked'
    | 'Quantify Templates - "Trend chart of top Tracked Keywords" clicked';
}

interface QuantifyReportVisualisationEventProps extends EventProps {
  eventName: 'Quantify Report - Change Visualisation';
  name: string;
  link: string;
  scope: Scope;
  newVisualisationType?: QuantifyVisualisationType;
  visualisationType?: QuantifyVisualisationType;
}

interface QuantifyReportViewFeedbackEventProps extends EventProps {
  eventName: 'Quantify Report - View Feedback';
  trigger: 'Metric' | 'Table';
  query: QuantifyQueryOptions;
  metricKeyLabel?: string;
  metricKeyId?: UUID;
}

interface QuantifyReportEventProps extends EventProps {
  eventName:
    | 'Quantify Report - Table Item Selection Changed'
    | 'Quantify Report - Sort By Updated'
    | 'Quantify Report - Table View Toggled'
    | 'Quantify Report - Download CSV'
    | 'Quantify Report - Search'
    | 'Quantify Report - Trend Chart Time-Period Changed'
    | 'Quantify Report - Show Co-occurring Toggled On'
    | 'Quantify Report - Show Co-occurring Toggled Off'
    | 'Quantify Report - Selected Top X Option'
    | 'Quantify Report - Reset To Top X Values'
    | 'Quantify Report - Dismissed Top X To Select Custom Values'
    | 'Quantify Report - Selected Custom Values'
    | 'Quantify Report - Share Link Copied';
  optionLabelIDs?: GridRowId[];
  searchText?: string;
  sortBy?: SortBy;
  shouldShowAllValues?: boolean;
  aggregatePeriod?: TimeUnit;
  query?: QuantifyQueryOptions;
  name?: string;
  link?: string;
  scope?: Scope;
  topXOption?: number;
  from?: string;
}

interface IntegrationEventProps extends EventProps {
  eventName: 'Add New Integration';
  integrationChannelType: IntegrationChannelType;
}

interface IntegrationEventV2Props extends EventProps {
  eventName:
    | 'Add New Integration Sidebar Opened'
    | 'Add New Integration Sidebar Closed'
    | 'Connect Integration CTA Clicked'
    | 'Edit Integration Sidebar Opened'
    | 'Edit Integration Sidebar Closed'
    | 'Integration Form Connect CTA Clicked'
    | 'Integration Added';
  integrationChannelType?: IntegrationChannelType;
}

interface SuggestionEventProps extends EventProps {
  eventName: 'Report Mistake' | 'Request Reasons';
  data: SuggestionDataInput;
  user: User;
}

interface SearchV2EventProps extends EventProps {
  eventName:
    | 'Search share link copied'
    | 'Recommended Keyword Clicked'
    | 'Recommended Reason Clicked'
    | 'Advanced Search Clicked'
    | 'Search triggered'
    | 'Search Date Range updated';
  url?: string;
  dateRange?: string;
  isEnterKeyTriggered?: boolean;
}

interface viewManageMetadataEvent extends EventProps {
  eventName: 'Viewing Manage Metadata';
  integrationChannelType: IntegrationChannelType;
}

interface previewManageMetadataFeedbackCard extends EventProps {
  eventName:
    | 'Manage Metadata - Preview Feedback Card Opened'
    | 'Manage Metadata - Preview Feedback Card Closed';
  integrationChannelType: IntegrationChannelType;
}

interface editMetadataEvent extends EventProps {
  eventName: 'Editing Metadata';
  metadata: {
    key: string;
    originalName: string;
    source?: string;
  };
}

interface EditIntegrationEvents {
  eventName: 'Edit Integration Submitted';
  channelType: IntegrationChannelType;
  integrationID: string;
}

interface metadataSaveEvent extends EventProps {
  eventName: 'Metadata Saved';
  metadata: UpdateMetadataRequest;
}

interface metadataUserIDChangeEvent extends EventProps {
  eventName: 'User Identifier Changed';
  isRemoved?: boolean;
  userID?: {
    key: string;
    originalName: string;
    source?: string;
  };
}

type ManageMetadataEvents =
  | viewManageMetadataEvent
  | previewManageMetadataFeedbackCard
  | editMetadataEvent
  | metadataSaveEvent
  | metadataUserIDChangeEvent;

interface OnboardTenantEvents extends EventProps {
  eventName: 'Onboard Tenant - All Integrations Added';
  tenant: {
    id: string;
    name: string;
    slug?: string;
  };
}

interface BrowseArtefactEvents extends EventProps {
  eventName: 'Artefact Clicked';
  type: ArtefactType[];
  filter: ArtefactSpecificFilter;
  status: Status[];
  editableBy: ArtefactEditableByFilter;
  artefactLink: string;
}

interface SearchDashboardEvents extends EventProps {
  eventName:
    | 'Search Dashboard Saved'
    | 'Search Dashboard Scope Changed'
    | 'Search Dashboard Renamed';
  artefactLink: string;
  name: string;
  scope: Scope;
}

interface KeyboardShortcutEvents extends EventProps {
  eventName: 'Saved Artefacts opened via keyboard shortcut';
}

interface SearchSubscribeEvents extends EventProps {
  eventName:
    | 'Search Subscribe - Drawer Opened'
    | 'Search Subscribe - Connect Slack Button Clicked'
    | 'Search Subscribe - Slack Channels Dropdown Opened'
    | 'Search Subscribe - Slack Channels Selected'
    | 'Search Subscribe - Subscription Added'
    | 'Search Subscribe - Subscription Updated'
    | 'Search Subscribe - Subscription Removed';
  // TODO: Remove redundant events after removing subscription FF
  isPDFReportingIncluded?: boolean;
  subscriptionType?: 'Feedback Stream' | 'Search Report';
  artefactLink: string;
  slackChannels?: SlackChannelInput[];
  emails?: string[];
}

interface FeedbackOnFeedbackEvents extends EventProps {
  eventName:
    | 'Edit Predictions View Opened'
    | 'Edit Predictions View Closed'
    | 'Submit Edited Predictions';
  recordID: string;
  pageUrl: string;
  editedPredictions?: PredictionManipulation[];
}

interface FeedbackOnFeedbackV2Events extends EventProps {
  eventName:
    | 'Add Predictions View Opened'
    | 'Add Predictions View Closed'
    | 'Prediction Added'
    | 'Prediction Removed'
    | 'Prediction Edited';
  toggleLocation: 'Feedback List View' | 'Feedback Detail View';
  feedbackID: string;
  predictionName?: string;
  predictionType?: 'Reason' | 'Tracked Keyword' | 'Sentiment';
  isSuggested?: boolean;
  edit?: {
    oldCategory?: Category;
    newCategory?: Category;
  };
}

interface FeedbackSummaryEvents extends EventProps {
  eventName:
    | 'Feedback List - Summary Toggled On'
    | 'Feedback List - Summary Toggled Off';
  route: string;
  state: 'Collapsed' | 'Expanded';
  toggleLocation: 'Feedback Drawer' | 'Search List';
}

interface AudioFeedbackEvents extends EventProps {
  eventName:
    | 'Audio Feedback - Player: Play Button Clicked'
    | 'Audio Feedback - Player: Pause Button Clicked'
    | 'Audio Feedback - Player: Playback Speed Changed'
    | 'Audio Feedback - Player: Rewind Button Clicked'
    | 'Audio Feedback - Player: Forward Button Clicked'
    | 'Audio Feedback - Player: Seekbar Bookmark Clicked'
    | 'Audio Feedback - Transcript: Toggled Off'
    | 'Audio Feedback - Transcript: Toggled On'
    | 'Audio Feedback - Transcript: Expanded To Full View'
    | 'Audio Feedback - Transcript: Collapsed To Subtitle View'
    | 'Audio Feedback - Transcript: Play Button Clicked'
    | 'Audio Feedback - Transcript: Share Button Clicked'
    | 'Audio Feedback - Highlights Bar: Next Button Clicked'
    | 'Audio Feedback - Highlights Bar: Prev Button Clicked'
    | 'Audio Feedback - Highlights Bar: Filters Popup Toggled'
    | 'Audio Feedback - Highlights Bar: Clear Button Clicked'
    | 'Audio Feedback - Summary List: Played Audio'
    | 'Audio Feedback - Share Snippet: Copied URL To Clipboard';
  feedbackID: string;
  triggerLocation?: 'Feedback List Card' | 'Feedback Detail Card';
}

export interface ExploreTabClickedEventProps extends EventProps {
  eventName: ExploreTabClickedEventName;
}

export interface UserManagementEventProps extends EventProps {
  eventName:
    | 'User Management - Opened User Permissions Modal'
    | 'User Management - Auto Approval Toggled On'
    | 'User Management - Auto Approval Toggled Off'
    | 'User Management - Invite Users: Opened Modal From Workspace Settings'
    | 'User Management - Invite Users: Opened Modal From Nav Sidebar'
    | 'User Management - Invite Users: Opened Modal From Tenant Onboarding'
    | 'User Management - Invite Users: Clicked On Send Email Button'
    | 'User Management - Invite Users: Clicked On Copy Link Button'
    | 'User Management - Invite Users: Selected User Role'
    | 'User Management - Converted User to Admin Role'
    | 'User Management - Converted User to Editor Role'
    | 'User Management - Suspended User From Workspace'
    | 'User Management - Reactivated User From Workspace'
    | 'User Management - Force Logged Out Suspended User'
    | 'User Management - Force Logged Out Unapproved User';
  userEmail?: string;
  invitedUserEmails?: string[];
}

interface DashboardEventQuery {
  duration?: string;
  whereClause?: string;
}

export interface DashboardGlobalFilterEventProps extends EventProps {
  eventName:
    | 'Dashboard Global Filter - DateRange Changed'
    | 'Dashboard Global Filter - WhereClause Changed'
    | 'Dashboard Global Filter - WhereClause Removed'
    | 'Dashboard Global Filter - Filters Saved'
    | 'Dashboard Global Filter - Add Filter Clicked'
    | 'Dashboard Global Filter - Show Filter Clicked'
    | 'Dashboard Global Filter - Hide Filter Clicked';
  query?: DashboardEventQuery;
}

interface SaveAsNewCTAEventProps extends EventProps {
  eventName:
    | 'Save As New CTA Clicked - Search page'
    | 'Save As New CTA Clicked - Quantify page'
    | 'Save As New CTA Clicked - Compare page';
  artefactName?: string;
  artefactViewToken?: string;
  isUserArtefactOwner?: boolean;
}

interface QuantifyCategoriesEventProps extends EventProps {
  eventName:
    | 'Quantify Categories - Toggled On'
    | 'Quantify Categories - Toggled Off';
  query?: QuantifyQueryOptions;
  name?: string;
  link?: string;
}

interface QuantifyReportAnomaliesEventProps extends EventProps {
  eventName:
    | 'Quantify Anomalies - Toggled On'
    | 'Quantify Anomalies - Toggled Off'
    | 'Quantify Anomalies - Set Threshold Popover Opened'
    | 'Quantify Anomalies - Set Threshold Popover Closed'
    | 'Quantify Anomalies - Custom Threshold Option Selected'
    | 'Quantify Anomalies - Custom Threshold Input Value Changed'
    | 'Quantify Anomalies - Default Threshold Option Selected'
    | 'Quantify Anomalies - Apply Threshold Button Clicked';
  anomalyThreshold?: number;
  query?: QuantifyQueryOptions;
  name?: string;
  link?: string;
}

interface QuantifyExportChartAsImageEventProps extends EventProps {
  eventName: 'Clicked Export As Image';
  chartType?: QuantifyVisualisationType;
  isSavedAnalysis: boolean;
  from?: string;
}

interface SaveFeedbackEventProps extends EventProps {
  eventName:
    | 'Save Feedback - Feedback Record Saved'
    | 'Save Feedback - Feedback Record Unsaved'
    | 'Save Feedback - Summary Snippet Saved'
    | 'Save Feedback - Summary Snippet Unsaved'
    | 'Save Feedback - My Saved Feedback Button Clicked';
  feedbackLink?: string;
  snippetLink?: string;
}

interface CopyFeedbackContentEvents extends EventProps {
  eventName: 'Copied Text From Feedback Content';
  contentType: 'raw' | 'summary';
  feedbackType: FeedbackContentType;
  feedbackID: string;
  pageURL?: string;
}

interface QuantifyCompareEventProps extends EventProps {
  eventName:
    | 'Quantify Compare - Compare Button Clicked'
    | 'Quantify Compare - By Time Option Selected'
    | 'Quantify Compare - By Another Filter Option Selected'
    | 'Quantify Compare - Remove Button Clicked'
    | 'Quantify Compare - Duration A Changed'
    | 'Quantify Compare - Duration B Previous Period Option Selected'
    | 'Quantify Compare - Duration B Custom Duration Option Selected'
    | 'Quantify Compare - Duration B Custom Duration Changed'
    | 'Quantify Compare - Filter A Changed'
    | 'Quantify Compare - Filter B Changed'
    | 'Quantify Compare - Add From Filter A Button Clicked'
    | 'Quantify Compare - Reset Filter Icon Button Clicked'
    | 'Quantify Compare - Quick Filter Added'
    | 'Quantify Compare - Quick Filter Added For Filter A'
    | 'Quantify Compare - Quick Filter Added For Filter B'
    | 'Quantify Compare - Filter A renamed'
    | 'Quantify Compare - Filter B renamed'
    | 'Quantify Compare - Duration A renamed'
    | 'Quantify Compare - Duration B renamed'
    | 'Quantify Compare - Opened Feedback Drawer';
  link: string;
  filterName?: string;
  from?: 'chart' | 'table';
}

interface FlagSummaryEvents extends EventProps {
  eventName:
    | 'Flagged as Incorrect'
    | 'Flagged as Missing'
    | 'Removed Incorrect flag'
    | 'Removed Missing flag';
  feedbackID: string;
  keywordSummaryText?: string;
}

interface FeedbackListSummary extends EventProps {
  eventName:
    | 'Feedback List Summary - Generate Summary Button Clicked'
    | 'Feedback List Summary - User Quote Opened'
    | 'Feedback List Summary - User Quote Closed'
    | 'Feedback List Summary - User Quote Previous Button Clicked'
    | 'Feedback List Summary - User Quote Next Button Clicked'
    | 'Feedback List Summary - Navigated to External Source in User Quote'
    | 'Feedback List Summary - Expand Button In User Quote Clicked'
    | 'Feedback List Summary - Manual Copy Summary of Summaries'
    | 'Feedback List Summary - Manual Copy User Quote'
    | 'Feedback List Summary - Copy Summary of Summaries'
    | 'Feedback List Summary - First token streamed'
    | 'Feedback List Summary - Last token streamed';
  copiedSummaryText?: string;
  copiedUserQuoteText?: string;
}

interface SearchSummary extends EventProps {
  eventName:
    | 'Search Summary - Copy Summary'
    | 'Search Summary - Generate Summary';
  copiedSummaryText?: string;
}

interface TimeRangeControlsEvents extends EventProps {
  eventName:
    | 'Time Range Controls - Pervious Button Clicked'
    | 'Time Range Controls - Next Button Clicked';
  originalDuration: string;
  currentDuration: string;
}

interface IntegrationPageEventsVNext extends EventProps {
  eventName:
    | 'Integrations - Selected Integration Source From List'
    | 'Integrations - Add New Button From Toolbar Clicked'
    | 'Integrations - Preview Feedback Card Button From Toolbar Clicked'
    | 'Integrations - Integration Actions Menu Opened'
    | 'Integrations - Disable Integration Option Clicked'
    | 'Integrations - Manage Metadata Card Expanded'
    | 'Integrations - Link Integrations Card Expanded'
    | 'Integrations - Add Integration Drawer: Selected Integration Type Filter Option'
    | 'Integrations - Add Integration Drawer: Clicked Integration Type To Connect'
    | 'Integrations - Edit Button Clicked';
  integration?: IntegrationDetails;
}

interface IntegrationPageManageMetadataEvents extends EventProps {
  eventName:
    | 'Manage Metadata Table - Edit Metadata Button Clicked'
    | 'Manage Metadata Table - Search Operation Performed'
    | 'Manage Metadata Drawer - Save Changes Button Clicked'
    | 'Manage Metadata Drawer - Next Page Button Clicked'
    | 'Manage Metadata Drawer - Previous Page Button Clicked';
  integration?: IntegrationDetails;
  updatedMetadata?: EditMetadataInput;
  searchQuery?: string;
}

interface IntegrationPageAddLinkEvents extends EventProps {
  eventName:
    | 'Link Integrations - Metadata Link Added'
    | 'Link Integrations - Metadata Link Removed'
    | 'Link Integrations - Feedback Matches Drawer Opened'
    | 'Link Integrations - Add New Integration Type Button Clicked';
  targetIntegrationField?: Partial<EqlField>;
  sourceIntegrationField?: Partial<EqlField>;
  integrationType?: IntegrationType;
}

interface FeedbackMetadataEvents extends EventProps {
  eventName:
    | 'Feedback Metadata - Feedback List Metadata Row Expanded'
    | 'Feedback Metadata - Show All Button Clicked'
    | 'Feedback Metadata - Show Less Button Clicked'
    | 'Feedback Metadata - Expanded Metadata Card Grouped By Identifier'
    | 'Feedback Metadata - Copied To Clipboard'
    | 'Feedback Metadata - Opened In Search Page';
  feedbackID: string;
  metadata?: {
    key: string;
    value: string;
  };
  objectType?: ObjectType;
}

interface CreateNewReasonEvents extends EventProps {
  eventName:
    | 'Create New Reason - Create New Reason Flow Triggered'
    | 'Create New Reason - Description Submitted'
    | 'Create New Reason - Description Prompt Edited'
    | 'Create New Reason - Go Back CTA Clicked'
    | 'Create New Reason - Minimize CTA Clicked'
    | 'Create New Reason - Maximize CTA Clicked'
    | 'Create New Reason - Regenerate CTA Clicked'
    | 'Create New Reason - Selected Reason Definition Removed'
    | 'Create New Reason - Reason Definition Added'
    | 'Create New Reason - Reason Definition Searched'
    | 'Create New Reason - Reason Created'
    | 'Create New Reason - Review Merge Suggestions CTA Clicked';
  from?:
    | 'Taxonomy'
    | 'Feedback Record'
    | 'Edit Reason - Breakout Into New Reason';
  selectedRC?: string;
  description?: string;
  newDescription?: string;
  reasonDefinition?: {
    type: 'SearchedUnassignedRc' | 'GeneratedRC' | 'UnassignedRC';
    name: string;
    category?: Category;
  };
  reasonDefinitionFilterTerm?: string;
  createdReason?: { name: string; category?: Category };
}

export interface FeedbackPaginationEventProps extends EventProps {
  eventName: 'Feedback Records Page Changed';
  page: number;
  view: 'Feedback List' | 'Feedback Details';
}

interface OpenInQuantifyEventProps extends EventProps {
  eventName: 'Feed Page - Open Top Reasons in Quantify Clicked';
}

export interface WisdomEventProps extends EventProps {
  eventName:
    | 'Wisdom - Input Submitted'
    | 'Wisdom - Input Edited'
    | 'Wisdom - Step Query Inspected'
    | 'Wisdom - Step Query Edited'
    | 'Wisdom - Save as Quantify'
    | 'Wisdom - Response Manually Copied'
    | 'Wisdom - Feedback'
    | 'Wisdom - See All Chats Button Clicked'
    | 'Wisdom - Sidebar Opened'
    | 'Wisdom - Sidebar Closed'
    | 'Wisdom - Chat Opened'
    | 'Wisdom - Preset Selected'
    | 'Wisdom - Rename Chat Clicked'
    | 'Wisdom - Chat Name Updated'
    | 'Wisdom - Chat Link Copied'
    | 'Wisdom - Chat Share Button Clicked'
    | 'Wisdom - Chat Archive Button Clicked'
    | 'Wisdom - Chat Archived'
    | 'Wisdom - Create New Chat'
    | 'Wisdom - Copy Response Clicked'
    | 'Wisdom - Manually Copy Response'
    | 'Wisdom - Hide Steps'
    | 'Wisdom - Show Steps'
    | 'Wisdom - View Feedback For Label Clicked'
    | 'Wisdom - View Quotes From Users Clicked'
    | 'Wisdom - Regenerate Response'
    | 'Wisdom - Stop Generating Response Clicked'
    | 'Wisdom - Error Occurred'
    | 'Wisdom - Opened Artefact View from Citation'
    | 'Wisdom - Opened Artefact Page from Feedback Citation View'
    | 'Wisdom - Copied Artefact Link from Feedback Citation View'
    | 'Wisdom - Clicked on Save As New Artefact from Feedback Citation View'
    | 'Wisdom - First Answer token Streamed'
    | 'Wisdom - Last Answer token Streamed';
  previousSearchText?: string;
  searchText?: string;
  threadPosition?: number;
  sessionID?: string;
  responseID?: string;
  stepType?: 'summary' | 'quantify';
  stepTitle?: string;
  stepEql?: string;
  stepPreviousEql?: string;
  isLastMessage?: boolean;
  isThumbUp?: boolean;
  stepEqlProps?: EqlQueryProperties;
  stepPrevEqlProps?: EqlQueryProperties;
  label?: string;
  from?: 'Header' | 'Chat List';
  chatName?: string;
  newChatName?: string;
  response?: string;
  error?: string;
  copiedFrom?: 'steps' | 'chat view' | 'details dialog';
  presetHeader?: string;
  presetType?: AssistantSampleQuestionType;
  artefact?: {
    id?: string;
    title?: string;
    useCase?: AnalysisUseCase;
    link?: string;
  };
  isFeedbackSidebarView?: boolean;
}

export interface UserInterestsEventProps extends EventProps {
  eventName: 'Users Interests Added';
  action: 'add' | 'update';
  from: 'onboarding' | 'homepage';
  userRole: UserRoleInput;
  workDescription: string;
}

export interface ExpandedFeedbackViewEventProps extends EventProps {
  eventName:
    | 'Feedback Record - Switched To Predictions Tab'
    | 'Feedback Record - Switched To Metadata Tab'
    | 'Feedback Record - Metadata Searched'
    | 'Feedback Record - Opened Metadata Value As URL'
    | 'Feedback Record - View Thread Button Clicked'
    | 'Feedback Record - Collapse Thread Button Clicked'
    | 'Feedback Record - View More Comments Button Clicked'
    | 'Feedback Record - Go Back to Main Thread Button Clicked';
  feedbackID: string;
  metadataSearchTerm?: string;
  metadataURL?: string;
}

export interface IntegrationConnectionEventProps extends EventProps {
  eventName:
    | `${IntegrationChannelType} connection successful`
    | `${IntegrationChannelType} connection failed`;
}

export interface ChatWithFeedbackEventProps extends EventProps {
  eventName:
    | 'Chat With Feedback - Copy Button Clicked'
    | 'Chat With Feedback - Thumbs Up Button Clicked'
    | 'Chat With Feedback - Thumbs Down Button Clicked'
    | 'Chat With Feedback - Regenerate Button Clicked'
    | 'Chat With Feedback - Go Button Clicked'
    | 'Chat With Feedback - Stop Generating Button Clicked';
  route: string;
  prompt?: string;
  isFeedbackSidebarView?: boolean;
}

export interface UserOnboardingEventProps extends EventProps {
  eventName: 'User Onboarding - Clicked on Skip Button';
  from: 'Welcome Screen' | 'Enterpret Intro Screen' | 'Wisdom Intro Screen';
}

export interface OpenInFeedEventProps extends EventProps {
  eventName: 'Open in Feed Button Clicked';
  openedFromURL: string;
  feedURL: string;
}

export interface EventMap {
  navbarItemClicked: NavbarItemClickedEventProps;
  exploreTabClicked: ExploreTabClickedEventProps;
  logout: LogoutEventProps;
  forceLogout: ForceLogoutEventProps;
  searchInput: SearchInputEventProps;
  datePicker: DatePickerEventProps;
  timeStepPicker: TimeStepPickerEventProps;
  downloadReport: DownloadReportEventProps;
  toggleNotebookView: ToggleNotebookViewEventProps;
  selectChartType: SelectChartTypeEventProps;
  feedbackViewAction: FeedbackViewActionEventProps;
  feedbackListSortBy: FeedbackListSortByEventProps;
  conversationView: ConversationViewEventProps;
  fetchRecords: FetchRecordsEventProps;
  fetchMoreRecords: FetchMoreRecordsEventProps;
  taxonomyTreeView: TaxonomyTreeViewEventProps;
  taxonomyTreeSelect: TaxonomyTreeSelectEventProps;
  notebook: NotebookEventProps;
  queryBuilder: QueryBuilderEventProps;
  quickFilter: QuickFilterEventProps;
  chartTooltip: ChartTooltipEventProps;
  widgetPagination: WidgetPaginationEventProps;
  taxonomyVisibility: TaxonomyVisibilityEventProps;
  autoFilter: AutoFilterEventProps;
  autoFilterClick: AutoFilterClickEventProps;
  searchQueryHistory: SearchQueryHistoryEventProps;
  toggleSearchRelatedQueriesView: ToggleSearchRelatedQueriesEventProps;
  signIn: SignInEventProps;
  signUpWithEmail: SignUpWithEmailEventProps;
  signUpWithGoogle: SignUpWithGoogleEventProps;
  otpViewNav: OTPViewNavEventProps;
  onboarding: OnboardingEventProps;
  forgotPassword: ForgotPasswordEventProps;
  complianceLinks: ComplianceLinkEventProps;
  themeUpdated: ThemeUpdatedEventProps;
  themeCreated: ThemeCreatedEventProps;
  themeDeleted: ThemeDeletedEventProps;
  redirecting: RedirectEventProps;
  queryContainer: QueryContainerEventProps;
  analysis: AnalysisEventProps;
  analysisReset: AnalysisResetEventProps;
  analysisSharing: AnalysisSharingEventProps;
  analysisArchive: AnalysisArchiveEventProps;
  analysisFeedback: AnalysisFeedbackEventProps;
  dashboardList: DashboardListEventProps;
  dashboardCreate: DashboardCreateEventProps;
  dashboardUpdate: DashboardUpdateEventProps;
  dashboardReport: DashboardReportEventProps;
  dashboardArchive: DashboardArchiveEventProps;
  viewDashboard: ViewDashboardEventProps;
  analysisSwitcher: AnalysisSwitcherEventProps;
  dashboardSharing: DashboardSharingEventProps;
  inviteUser: InviteUser;
  qbAction: QueryBuilderActionEventProps;
  commandPalette: CommandPaletteEventProps;
  semanticSearch: SemanticSearchEventProps;
  ruleSharing: RuleSharingEventProps;
  clearCache: ClearCacheEventProps;
  feedbackEvents: FeedbackEventsProps;
  statusEmailEvents: StatusEmailEventsProps;
  onboardingEvents: OnboardingEventsProps;
  analysisV3: AnalysisV3EventProps;
  analysisV3Reset: AnalysisV3ResetEventProps;
  analysisV3Sharing: AnalysisV3SharingEventProps;
  analysisV3Archive: AnalysisV3ArchiveEventProps;
  analysisV3Opened: AnalysisV3OpenedEventProps;
  createNewAnalysisV3: CreateNewAnalysisV3EventProps;
  analysisV3Search: AnalysisV3SearchEventProps;
  analysisV3ListTab: AnalysisV3ListTabEventProps;
  analysisV3ListTabPagination: AnalysisV3ListTabPaginationEventProps;
  inAppNotificationsList: InAppNotificationListEventProps;
  inAppNotificationCard: InAppNotificationCardEventProps;
  notificationSettings: NotificationSettingsEventProps;
  integration: IntegrationEventProps;
  integrationV2: IntegrationEventV2Props;
  editIntegration: EditIntegrationEvents;
  suggestion: SuggestionEventProps;
  searchV2: SearchV2EventProps;
  manageMetadata: ManageMetadataEvents;
  onboardTenant: OnboardTenantEvents;
  browseArtefact: BrowseArtefactEvents;
  searchDashboard: SearchDashboardEvents;
  keyboardShortcut: KeyboardShortcutEvents;
  searchSubscribe: SearchSubscribeEvents;
  quantifyQuery: QuantifyQueryEventProps;
  quantifyReportVisualisation: QuantifyReportVisualisationEventProps;
  quantifyReportViewFeedback: QuantifyReportViewFeedbackEventProps;
  quantifyReport: QuantifyReportEventProps;
  feedbackOnFeedback: FeedbackOnFeedbackEvents;
  feedbackOnFeedbackV2: FeedbackOnFeedbackV2Events;
  dashboardV2: DashboardV2Events;
  feedbackSummary: FeedbackSummaryEvents;
  audioFeedback: AudioFeedbackEvents;
  taxonomyV2: TaxonomyV2Events;
  searchAnalysesWidgets: SearchAnalysesWidgetsEvents;
  userManagement: UserManagementEventProps;
  dashboardGlobalFilter: DashboardGlobalFilterEventProps;
  saveAsNewCTA: SaveAsNewCTAEventProps;
  quantifyAnomalies: QuantifyReportAnomaliesEventProps;
  quantifyCategories: QuantifyCategoriesEventProps;
  quantifyExportChartAsImage: QuantifyExportChartAsImageEventProps;
  quantifyTemplates: QuantifyTemplatesEventProps;
  saveFeedback: SaveFeedbackEventProps;
  copyFeedbackContent: CopyFeedbackContentEvents;
  quantifyCompare: QuantifyCompareEventProps;
  flagSummary: FlagSummaryEvents;
  customFilter: CustomFilterEvents;
  feedbackListSummary: FeedbackListSummary;
  searchSummary: SearchSummary;
  timeRangeControls: TimeRangeControlsEvents;
  integrationPageVNext: IntegrationPageEventsVNext;
  integrationPageManageMetadata: IntegrationPageManageMetadataEvents;
  integrationPageAddLink: IntegrationPageAddLinkEvents;
  feedbackMetadata: FeedbackMetadataEvents;
  createNewReason: CreateNewReasonEvents;
  feedbackPagination: FeedbackPaginationEventProps;
  wisdom: WisdomEventProps;
  userInterests: UserInterestsEventProps;
  collection: CollectionEvents;
  openInQuantify: OpenInQuantifyEventProps;
  expandedFeedbackView: ExpandedFeedbackViewEventProps;
  integrationConnection: IntegrationConnectionEventProps;
  chatWithFeedback: ChatWithFeedbackEventProps;
  userOnboarding: UserOnboardingEventProps;
  openInFeed: OpenInFeedEventProps;
}

type EventType = keyof EventMap;

export const createAnalyticsTracker = <K extends EventType>(_: K) => {
  return (eventProps: EventMap[K]): void => {
    const { eventName, ...properties } = eventProps;

    const propertiesWithAdditionalInfo = {
      ...properties,
      URL: window?.location?.href,
    };

    if (shouldSendAnalyticsEvents() && window.analytics) {
      try {
        window.analytics.track(
          eventName,
          propertiesWithAdditionalInfo,
          getDefaultAnalyticsOptions(),
        );
        Userpilot.track(eventName, propertiesWithAdditionalInfo);
      } catch (e) {
        captureMessage(`Failed to track analytics event: ${eventName}`);
      }
    } else {
      if (shouldLogAnalyticsEvents()) {
        console.log('TRACK ->', eventName, propertiesWithAdditionalInfo);
      }
    }
  };
};
